/**
 * Which announcements the viewer dismissed (phase 7b's in-app banner): a
 * per-browser convenience in localStorage, keyed by id AND version — an edit
 * of an announcement (a worse severity, an update to an incident) shows it
 * again. Storage may be absent or throw (private mode, blocked site data):
 * every read and write is guarded, and a failure only means the banner comes
 * back on the next page load.
 */
export const DISMISSALS_STORAGE_KEY = 'acp.dismissedAnnouncements';

/** How many dismissals are kept: the oldest fall off, so the entry cannot grow without bound. */
export const MAX_DISMISSALS = 200;

export type KeyValue = { readonly get: (key: string) => string | null; readonly set: (key: string, value: string) => void };

/** `<id>@<version>`: what one dismissal records. */
export function dismissalKey(announcement: { readonly announcementId: string; readonly version: number }): string {
  return `${announcement.announcementId}@${String(announcement.version)}`;
}

/** The stored list, or empty when absent, unreadable or not a list of strings. */
export function parseDismissals(raw: string | null): readonly string[] {
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

/** The list after dismissing `key`: appended once, the oldest dropped past `MAX_DISMISSALS`. */
export function withDismissal(list: readonly string[], key: string): readonly string[] {
  if (list.includes(key)) return list;
  const next = [...list, key];
  return next.length > MAX_DISMISSALS ? next.slice(next.length - MAX_DISMISSALS) : next;
}

/** The announcements still to show. */
export function undismissed<T extends { readonly announcementId: string; readonly version: number }>(announcements: readonly T[], dismissed: readonly string[]): T[] {
  const set = new Set(dismissed);
  return announcements.filter((a) => !set.has(dismissalKey(a)));
}

export type DismissalStore = {
  /** The same array until a dismissal changes it (a `useSyncExternalStore` snapshot). */
  readonly get: () => readonly string[];
  readonly dismiss: (key: string) => void;
  readonly subscribe: (listener: () => void) => () => void;
};

export function createDismissalStore(storage: KeyValue): DismissalStore {
  const read = (): readonly string[] => {
    try {
      return parseDismissals(storage.get(DISMISSALS_STORAGE_KEY));
    } catch {
      return [];
    }
  };
  let current = read();
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    dismiss: (key) => {
      const next = withDismissal(current, key);
      if (next === current) return;
      current = next;
      try {
        storage.set(DISMISSALS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable: dismissed for this page's life only.
      }
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
